import {
  useDashboardAccountUrl,
  useDashboardLanguageUnitUrl,
} from "@/utils/redirects";
import { DistanceUnitTypes, validDistanceUnitKey } from "@faro-lotv/foundation";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { Dialog as MUIDialog } from "@mui/material";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { useEffect, useMemo, useState, type JSX } from "react";
import { fetchDistanceUnitSetting } from "../common/settings-context/distance-unit-utils";
import { useSettingsContext } from "../common/settings-context/settings-context";

interface Props {
  /** Callback function triggers when account and setting page should be closed */
  onClose(): void;

  /** Which tab to open in the account settings page */
  openTab?: "accountAndSecurity" | "languageAndUnit";
}

type CloseSettingPageFunction = () => void;

/**
 * @returns Dialog which opens account settings page
 */
export function AccountSettingsDialog({
  onClose,
  openTab = "accountAndSecurity",
}: Props): JSX.Element | null {
  const accountAndSecurityUrl = useDashboardAccountUrl();
  const languageAndUnitUrl = useDashboardLanguageUnitUrl();

  useSettingsPageListener(onClose);

  const settingUrl = useMemo(() => {
    switch (openTab) {
      case "accountAndSecurity":
        return accountAndSecurityUrl;
      case "languageAndUnit":
        return languageAndUnitUrl;
    }
  }, [accountAndSecurityUrl, languageAndUnitUrl, openTab]);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    // Open the account setting in this overlay if the url is pointing to Sphere Dashboard
    // Otherwise open as before in a new tab
    if (settingUrl?.includes("sphere.")) {
      setIsOpen(true);
    } else {
      window.open(settingUrl, "_blank");
    }
  }, [settingUrl, onClose]);

  if (!settingUrl) {
    return null;
  }

  return (
    <MUIDialog
      open={isOpen}
      sx={{
        height: "100vh",
        maxHeight: "100vh",
        width: "100vw",
        maxWidth: "100vw",
      }}
      slotProps={{
        paper: {
          sx: {
            height: "100%",
            maxHeight: "100%",
            maxWidth: "100%",
            width: "100%",
            padding: 0,
            margin: 0,
          },
        },
      }}
    >
      <iframe
        height="100%"
        width="100%"
        style={{ border: "0" }}
        src={settingUrl}
      />
    </MUIDialog>
  );
}

/**
 * Hook to listen for messages from the account settings page
 *
 * @param onClose Callback function triggers when account and setting page should be closed
 */
function useSettingsPageListener(onClose: CloseSettingPageFunction): void {
  const { setDistanceUnit } = useSettingsContext();
  const { settingsApiClient } = useApiClientContext();
  const { isLoggedIn } = useAuthContext();

  useEffect(() => {
    function settingsPageListener(
      event: MessageEvent<Partial<SettingsApiTypes.SettingPageMessage>>,
    ): void {
      // Validate that the message has the expected structure before processing
      if (!SettingsApiTypes.isSettingPageMessage(event.data)) {
        // Silently ignore invalid messages to prevent malformed/malicious messages from breaking the code
        return;
      }

      const validatedMessage = event.data;

      switch (validatedMessage.action) {
        case "closeSettingPage":
          onClose();

          if (isLoggedIn) {
            // Refresh the unit setting in case it is changed in the account setting page
            fetchDistanceUnitSetting(settingsApiClient).then((unitType) => {
              if (unitType !== undefined) {
                setDistanceUnit(unitType);
              }
            });
          }
          break;

        case "updateSetting": {
          if (SettingsApiTypes.isSettingPageMessage(validatedMessage)) {
            const { settingIdentifier, settingValue } = validatedMessage.data;
            // Only string-valued settings are persisted here; object-valued
            // settings (e.g. table column preferences) are handled elsewhere.
            if (typeof settingValue !== "string") {
              break;
            }
            localStorage.setItem(settingIdentifier, settingValue);

            // If the distance unit setting is changed, update it in the context
            if (validDistanceUnitKey(settingValue)) {
              setDistanceUnit(DistanceUnitTypes[settingValue]);
            }
          }
          break;
        }
      }
    }

    window.addEventListener("message", settingsPageListener);

    return () => {
      window.removeEventListener("message", settingsPageListener);
    };
  }, [isLoggedIn, onClose, setDistanceUnit, settingsApiClient]);
}
